import React, { useState } from 'react'
import { Form, Slot } from '@slashid/react'
import { Link } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'

import CustomInput from 'components/elements/inputs/CustomInput'
import CustomButton from 'components/elements/buttons/CustomButton'
import MaintenanceBlock from 'components/elements/blocks/MaintenanceBlock'

import {
  Check,
  Logotype,
  ReviewG2,
  ReviewCapterra,
  /*LightningAlt as LightningIcon,*/
} from 'components/icons'

import { UserActionsTypes } from 'store/user/actions'
import { logAmplitudeEvent } from 'utils/analytics'
import { selectMaintenanceModeStatus } from 'store/service/selectors'

import styles from '../AuthPage.module.scss'

const tools = [
  {
    title: 'Residential, mobile, datacenter, and ISP proxies',
    description:
      'Access geo-restricted content and avoid blocks with +191 million ethically sourced IPs across 195+ countries, sticky sessions, and city-level targeting',
  },
  {
    title: 'Web Unblocker',
    description:
      'Automatically bypass CAPTCHAs, WAFs, and anti-bot systems for seamless global data collection',
  },
  {
    title: 'Scraper APIs',
    description:
      'Extract structured data from global search engines and ecommerce sites - no technical skills needed',
  },
]

const SignUpPage = () => {
  // @ts-ignore
  const dataLayer = window.dataLayer

  const isMaintenanceMode = useSelector(selectMaintenanceModeStatus)

  const handleFormSuccess = (user: any) => {
    const lastAuthMethod = user.authentications[0].method

    logAmplitudeEvent('login success', {
      form: 'sign_up',
      authMethod: lastAuthMethod === 'otp_via_email' ? 'email' : 'google',
    })

    if (user.firstLogin) {
      dataLayer.push({ event: 'signup_completed' })
    } else {
      dataLayer.push({ event: 'login_completed' })
    }
  }

  const handleFormError = () => {
    logAmplitudeEvent('login fail', {
      form: 'sign_up',
    })
  }

  const toolsList = tools.map((tool, index) => (
    <li className={styles.tool} key={`tool-item_${index}`}>
      <div className={styles.toolIconWrapper}>
        <Check />
      </div>

      <div>
        <div className={styles.toolName}>{tool.title}</div>
        <div className={styles.toolDescription}>{tool.description}</div>
      </div>
    </li>
  ))

  return (
    <div className={styles.wrapper}>
      <Helmet>
        <title>Sign Up</title>
        <meta
          name="description"
          content="Create a SOAX account and get access to proxies, web unblocker, and more"
        />
      </Helmet>

      <div className={styles.leftBlock}>
        <a href={'https://soax.com/'} className={styles.logoWrapper}>
          <Logotype />
        </a>

        <div className={styles.description}>
          Unlock unrestricted web access and collect data at scale
        </div>

        <ul className={styles.tools}>{toolsList}</ul>

        {/*<div
          className={styles.promoBlock}
          style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/fire.png)` }}
        >
          <div className={styles.promoHeader}>
            <div className={styles.promoIcon}>
              <LightningIcon />
            </div>
            <div className={styles.promoTitle}>
              Unlock your Black Friday deal
            </div>
          </div>

          <div className={styles.promoDesc}>
            Sign up now to get 250 GB of premium proxy for just $499!
          </div>
        </div>*/}

        <div className={styles.reviewsTitle}>
          Trusted by 10,000+ teams worldwide
        </div>

        <div className={styles.reviews}>
          <div className={styles.reviewItem}>
            <ReviewG2 />
          </div>
          <div className={styles.reviewItem}>
            <ReviewCapterra />
          </div>
        </div>
      </div>
      <div className={styles.rightBlock}>
        <div className={styles.formWrapper}>
          <div className={styles.formBackground1} />
          <div className={styles.formBackground2} />

          {isMaintenanceMode ? (
            <div className={styles.formContainer}>
              <MaintenanceBlock />
            </div>
          ) : (
            <div className={styles.formContainer}>
              <h1 className={styles.title}>Start collecting data in minutes</h1>

              <div className={styles.form}>
                <Form onSuccess={handleFormSuccess} onError={handleFormError}>
                  <Slot name="initial">
                    <Form.Initial.SSO />

                    <div className={styles.separator}>
                      <div className={styles.separatorLine} />
                      or
                      <div className={styles.separatorLine} />
                    </div>

                    <Form.Initial.Controls>
                      {({ handleSubmit, factors, handleTypes }) => {
                        return (
                          <CustomForm
                            handleSubmit={handleSubmit}
                            factors={factors}
                            handleTypes={handleTypes}
                          />
                        )
                      }}
                    </Form.Initial.Controls>
                  </Slot>

                  <Slot name="footer">
                  </Slot>
                </Form>
              </div>

              <div className={styles.loginBlock}>
                Already have an account?{' '}
                <Link
                  to={{ pathname: '/sign-in', search: window.location.search }}
                  className={styles.loginLink}
                >
                  Login
                </Link>
              </div>
            </div>
          )}
        </div>

        <div className={styles.bottomNotice}>
          By signing up, I agree to Minimal{' '}
          <a
            href="https://soax.com/legal/terms"
            target={'_blank'}
            rel={'noopener noreferrer'}
          >
            Terms of Service
          </a>{' '}
          and{' '}
          <a
            href="https://soax.com/legal/privacy"
            target={'_blank'}
            rel={'noopener noreferrer'}
          >
            Privacy Policy.
          </a>
        </div>
      </div>
    </div>
  )
}

interface ICustomFormProps {
  handleSubmit: any
  factors: any
  handleTypes: any
}

const CustomForm: React.FC<ICustomFormProps> = (props) => {
  const dispatch = useDispatch()

  const { handleSubmit } = props

  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  // factors = [{method: "email_link"}]
  // handleTypes = ["email_address"]

  const handleContinueClick = (e: any) => {
    e.preventDefault()

    logAmplitudeEvent('login clicked', {
      form: 'sign_up',
      authMethod: 'email_and_code',
    })

    dispatch({
      type: UserActionsTypes.USER_SET_TEMP_FIRST_NAME,
      tempFirstName: firstName,
    })
    dispatch({
      type: UserActionsTypes.USER_SET_TEMP_LAST_NAME,
      tempLastName: lastName,
    })

    // if (Errors) { console.log('Errors', Errors)}

    // we only wanted to use the email_link method, otherwise we could have used the handleTypes and the factors to determine the input type
    handleSubmit(
      { method: 'otp_via_email' },
      { type: 'email_address', value: email }
    )
  }

  return (
    <form onSubmit={handleContinueClick} id={'sign_up-form'}>
      <div className={styles.inputs}>
        <CustomInput
          className={styles.input}
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="First name"
          inputSize={'l'}
        />

        <CustomInput
          className={styles.input}
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          placeholder="Last name"
          inputSize={'l'}
        />

        <CustomInput
          className={styles.input}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          inputSize={'l'}
        />
      </div>

      <CustomButton
        type={'submit'}
        wide
        largeRadius
        size={'l'}
        disabled={!email || !firstName || !lastName}
      >
        Get started
      </CustomButton>
    </form>
  )
}

export default SignUpPage
